import React from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useLazyQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import cn from 'clsx';
import styles from './styles.module.scss';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import Loading from '@components-clean/core/loading/loading';
import { useTranslation } from '@hooks/index';
import { GET_DATA_VIEW_CONFIGURATIONS } from '@queries/central-monitoring';
import {
    dataViewConfigurations,
    dataViewConfiguration_OpraQueries_dataViewConfiguration_DataViewConfiguration,
    dataViewConfigurationsVariables,
} from '@queries/generated/central-monitoring.generated';

export type PanelDataViews = {
    reference: string;
    title: string;
    dataViewRef?: string;
    active: boolean;
};

type Props = {
    onClose: () => void;
    onSubmit: (values: PanelDataViews[]) => void;
    panels: PanelDataViews[];
    studyRef: string;
};

type FormData = {
    associations: (PanelDataViews & {
        dataView: { id?: string; name?: string | null } | null;
    })[];
};

const ROW_HEIGHT = 90;
const HEADER_HEIGHT = 33;
const FOOTER_HEIGHT = 33;
const ROW_PADDING_BOTTOM = 10;

const calculateDialogHeight = (noOfDataViews: number) => {
    let height = Math.min(noOfDataViews, 8) * ROW_HEIGHT + HEADER_HEIGHT + FOOTER_HEIGHT;
    if (noOfDataViews > 1) height -= ROW_PADDING_BOTTOM;
    return height;
};

export const DataViewAssociationDialog = ({ onClose, onSubmit, panels, studyRef }: Props) => {
    const { t } = useTranslation();

    const [fetch, { data: dataViewConfigs, loading: configsLoading }] = useLazyQuery<
        dataViewConfigurations,
        dataViewConfigurationsVariables
    >(GET_DATA_VIEW_CONFIGURATIONS);

    const {
        control,
        handleSubmit,
        formState: { isDirty },
    } = useForm<FormData>({
        defaultValues: async () => {
            const configs = await fetch({
                variables: { studyId: studyRef },
            });

            return {
                associations: panels.map((x) => {
                    const dataViewConfig = x.dataViewRef
                        ? configs.data?.dataViewConfigurations?.find((d) => d?.id === x.dataViewRef)
                        : null;
                    return {
                        ...x,
                        dataView: x.dataViewRef
                            ? {
                                  id: x.dataViewRef,
                                  name: dataViewConfig?.name,
                                  active: dataViewConfig?.isActive,
                              }
                            : null,
                    };
                }),
            };
        },
    });

    const { fields } = useFieldArray({
        control,
        name: 'associations',
    });

    const transformAndSubmit = (data: FormData) => {
        onSubmit(
            data.associations.map<PanelDataViews>((x) => ({
                reference: x.reference,
                title: x.title,
                dataViewRef: x.dataView?.id,
                active: x.active,
            })),
        );
    };

    return (
        <DraggableDialog
            dataTestId="data-view-association-dialog"
            title={t('cm:controlsHub.dataViewAssociation.title')}
            initialWidth={620}
            initialHeight={calculateDialogHeight(panels.length)}
            primaryBtnLabel={t('update')}
            primaryBtnDisabled={!isDirty}
            secondaryBtnLabel={t('cancel')}
            onClose={onClose}
            onSubmit={() => handleSubmit((data) => transformAndSubmit(data))()}
        >
            {configsLoading ? (
                <Loading />
            ) : (
                <form style={{ width: '100%', height: '100%' }}>
                    {fields.map((item, index) => (
                        <div key={item.id} className={styles.panelAssociationRow}>
                            <div className="k-d-flex k-align-items-end k-gap-3">
                                <div
                                    className={cn(
                                        'k-d-flex k-align-items-center panel-title k-align-content-center',
                                        styles.panelTitle,
                                    )}
                                >
                                    {item.title}
                                </div>
                                <FontAwesomeIcon className={styles.associationIcon} icon={['fas', 'link']} />
                                <div style={{ width: '350px' }}>
                                    <BasicDropDownController
                                        control={control}
                                        name={`associations.${index}.dataView`}
                                        data-testid={`data-view-dropdown-${index}`}
                                        data={
                                            dataViewConfigs?.dataViewConfigurations
                                                ?.filter(
                                                    (
                                                        x,
                                                    ): x is dataViewConfiguration_OpraQueries_dataViewConfiguration_DataViewConfiguration =>
                                                        !!x,
                                                )
                                                .map((x) => ({
                                                    id: x.id,
                                                    name: x.name ?? '',
                                                    groupedBy: x.isActive ? t('active') : t('inactive'),
                                                    disabled: item.dataViewRef !== x.id && !x.isActive,
                                                    active: x.isActive,
                                                }))
                                                .sort((a, b) => {
                                                    if (a.active !== b.active) {
                                                        return a.active ? -1 : 1;
                                                    }

                                                    return a.name.localeCompare(b.name);
                                                }) ?? []
                                        }
                                        textField="name"
                                        dataItemKey="id"
                                        groupField="groupedBy"
                                        activeField="active"
                                        label={t('cm:controlsHub.dataViewAssociation.dataView')}
                                        className="k-flex-auto"
                                        allowClear
                                        filterable
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </form>
            )}
        </DraggableDialog>
    );
};
